// The option list for the community-management escalation-contact picker
// (EscalationContactPicker in AccountForm). Pure so the job-title rule is
// asserted without jsdom.
//
// ⚠️ THE JOB TITLE IS PER ACCOUNT LINK, NOT PER CONTACT. `jobTitle` lives on
// `Contact.accountLinks[]` (the same person can be "Sales Manager" at one
// dealership and "Dealer Principal" at another), so the description is read
// off the link for THIS account. Reading any other link would show a title the
// person does not hold here.
//
// ⚠️ The Combobox searches over `description` too, so typing "Service Manager"
// finds them — the point of the change.
//
// ℹ️ PRE-EXISTING, NOT FIXED HERE: `computeDisplayName` composes
// `[title, firstName, lastName]`, so a contact with an honorific and no first
// name is labelled exactly "Mr" (the client portal hit the same case — see
// src/lib/portalGreeting.ts). A different change with its own decision.

import { computeDisplayName, type Contact } from '../../types/contact';
import type { ComboboxOption } from '../../components/ui/Combobox';

export function escalationContactOptions(
  accountId: string,
  contacts: Contact[],
  value: string | null,
): ComboboxOption[] {
  const known = new Set(contacts.map((c) => c.id));
  return [
    { value: '', label: '— Unassigned —' },
    // A stored id that isn't in the account's linked list is preserved as its
    // own option so editing never silently drops it.
    ...(value && !known.has(value) ? [{ value, label: `(linked contact: ${value})` }] : []),
    ...contacts.map((c) => {
      const jobTitle = c.accountLinks.find((l) => l.accountId === accountId)?.jobTitle?.trim();
      return {
        value: c.id,
        label: computeDisplayName(c),
        ...(jobTitle ? { description: jobTitle } : {}),
      };
    }),
  ];
}
